<ng-template #template>
  <div class="modal-header">
    <h4 class="modal-title pull-left">{{ 'practicePlan.title' | translate }}</h4>
    <button type="button" class="close pull-right" [attr.aria-label]="'modal.close' | translate" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>
  <div class="modal-body practice-plan-body">
    <form [formGroup]="form">
      <div class="row">
        <div class="col-md-4">
          <h5>{{ 'practicePlan.commonAttributes' | translate }}</h5>
          <div class="form-group">
            <label>{{ 'schedule.team' | translate }} <span class="text-danger">*</span></label>
            <ng-select appearance="outline" formControlName="team">
              @for (team of teams; track team.path) {
                <ng-option [value]="team.path">{{team.name}}</ng-option>
              }
            </ng-select>
            @if (form.get('team')?.errors?.required && submitted) {
              <div class="invalid-tooltip d-block">{{ 'practicePlan.errors.teamRequired' | translate }}</div>
            }
          </div>
          @if (videoTool === 'youtube') {
            <div class="form-group">
              <label>{{ 'schedule.videoPrivacy' | translate }}</label>
              <select class="form-control" formControlName="videoPrivacy">
                @for (option of privacyOptions; track option.value) {
                  <option [value]="option.value">{{ option.displayValue | translate }}</option>
                }
              </select>
            </div>
          }
          @if (videoTool === 'hudl') {
            <div class="form-group">
              <label>{{ 'schedule.hudlEvent' | translate }}</label>&nbsp;@if (form.get('team')?.value) {
                <button type="button" (click)="refreshHudlEvents()" class="btn no-padding iconsminds-repeat-2"></button>
              }
              <ng-select appearance="outline" [items]="hudlEvents" bindLabel="name" bindValue="id"
                [searchable]="false" [loading]="hudlEventsLoading" formControlName="hudlEvent">
                <ng-template ng-option-tmp let-item="item">
                  <div>{{item.name}}</div>
                  <small>{{item.time | date: 'yyyy-MM-dd'}}</small>
                </ng-template>
              </ng-select>
            </div>
            <div class="form-group">
              <label>{{ 'schedule.hudlShare' | translate }}</label>&nbsp;@if (form.get('team')?.value) {
                <button type="button" (click)="refreshHudlMembersAndGroups()" class="btn no-padding iconsminds-repeat-2"></button>
              }
              <ng-select [loading]="hudlAvailableShareListLoading" [items]="hudlAvailableShareList"
                [hideSelected]="true" [multiple]="true" bindLabel="displayValue" bindValue="value"
                [closeOnSelect]="false" formControlName="hudlShare">
              </ng-select>
            </div>
          }
        </div>

        <div class="col-md-4">
          <h5>{{ 'practicePlan.planSettings' | translate }}</h5>
          <div class="form-row">
            <div class="form-group col">
              <label>{{ 'practicePlan.bufferBefore' | translate }}</label>
              <input type="number" min="0" class="form-control" formControlName="bufferBefore" />
            </div>
            <div class="form-group col">
              <label>{{ 'practicePlan.bufferAfter' | translate }}</label>
              <input type="number" min="0" class="form-control" formControlName="bufferAfter" />
            </div>
          </div>
          @if (availableCategories.length > 0) {
            <div class="form-group">
              <label>{{ 'practicePlan.categoriesIncluded' | translate }}</label>
              <div>
                @for (category of availableCategories; track category) {
                  <button type="button" class="btn btn-sm mr-1 mb-1"
                    [class.btn-primary]="!isExcluded(category)"
                    [class.btn-outline-secondary]="isExcluded(category)"
                    (click)="toggleCategory(category)">
                    {{ category }}
                  </button>
                }
              </div>
              <small class="text-muted">{{ 'practicePlan.categoriesHint' | translate }}</small>
            </div>
          }
        </div>

        <div class="col-md-4">
          <h5>{{ 'practicePlan.file' | translate }}</h5>
          <div class="form-group">
            <label>{{ 'practicePlan.fileFormat' | translate }}</label>
            <input type="file" class="form-control-file" accept=".pdf,.xlsx"
              (change)="onFileSelected($event)" [disabled]="!form.get('team')?.value" />
            @if (!form.get('team')?.value) {
              <small class="text-muted d-block">{{ 'practicePlan.selectTeamFirst' | translate }}</small>
            }
            @if (selectedFile) {
              <small class="d-block">{{ selectedFile.name }} ({{ (selectedFile.size / 1024 / 1024) | number: '1.1-2' }} MB)</small>
            }
            @if (fileError) {
              <div class="invalid-tooltip d-block">{{ fileError }}</div>
            }
          </div>
          <div class="form-group">
            <button type="button" class="btn btn-primary mr-2"
              [disabled]="!selectedFile || uploading || parsing"
              (click)="onLoadPlan()">
              @if (uploading) { <span>{{ 'practicePlan.uploading' | translate }}</span> }
              @else if (parsing) { <span>{{ 'practicePlan.parsing' | translate }}</span> }
              @else { <span>{{ 'practicePlan.loadPlan' | translate }}</span> }
            </button>
          </div>
          @if (generalError) {
            <div class="alert alert-danger">{{ generalError }}</div>
          }
        </div>
      </div>

      <hr />

      <h5>{{ 'practicePlan.practicesHeading' | translate: { count: practicesArray.length, total: totalRowsCount() } }}</h5>

      <div formArrayName="practices">
        @for (practice of practicesArray.controls; track practice; let pi = $index) {
          <div class="practice-section card p-3 mb-3" [formGroupName]="pi">
            @if (practice.get('sourceLabel')?.value) {
              <div class="text-muted small mb-2">
                <i class="simple-icon-doc mr-1"></i>{{ practice.get('sourceLabel')?.value }}
              </div>
            }
            <div class="row mb-3">
              <div class="col-md-4 col-sm-6">
                <div class="form-group mb-0">
                  <label>{{ 'practicePlan.practiceNumberDate' | translate: { n: pi + 1 } }} <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" formControlName="practiceDate" bsDatepicker
                    [container]="'html'" placement="bottom left"
                    [bsConfig]="{ adaptivePosition: false, dateInputFormat: 'YYYY-MM-DD', showWeekNumbers: false }" />
                  @if (practice.get('practiceDate')?.errors?.required && submitted) {
                    <div class="invalid-tooltip d-block">{{ 'practicePlan.practiceDateRequired' | translate }}</div>
                  }
                </div>
              </div>
              @if (practicesArray.length > 1) {
                <div class="col-auto align-self-end">
                  <button type="button" class="btn btn-link text-danger" (click)="removePractice(pi)">
                    <i class="simple-icon-close mr-1"></i>{{ 'practicePlan.removePractice' | translate }}
                  </button>
                </div>
              }
            </div>

            @if (practiceRowsAt(pi).length === 0) {
              <p class="text-muted">{{ 'practicePlan.noRowsForPractice' | translate }}</p>
            }

            <div formArrayName="rows">
              @for (row of practiceRowsAt(pi).controls; track row; let i = $index) {
                <div class="position-relative">
                  <button type="button" class="btn btn-link btn-sm text-danger row-remove" (click)="removeRow(pi, i)" [title]="'practicePlan.row.remove' | translate">
                    <i class="simple-icon-close"></i>
                  </button>
                  <app-practice-plan-row
                    [rowForm]="$any(row)"
                    [recordingSetups]="recordingSetups"
                    [recordingSetupsLoading]="recordingSetupsLoading"
                    [defaultBufferBefore]="form.get('bufferBefore')?.value"
                    [defaultBufferAfter]="form.get('bufferAfter')?.value"
                    [index]="i"
                    [submitted]="submitted">
                  </app-practice-plan-row>
                  @if (i < practiceRowsAt(pi).length - 1) {
                    <div class="text-center my-1">
                      <button type="button" class="btn btn-outline-secondary btn-xs" (click)="mergeWithNext(pi, i)" [title]="'practicePlan.mergeWithNext' | translate">
                        <i class="simple-icon-arrow-down mr-1"></i>{{ 'practicePlan.mergeWithNext' | translate }}
                      </button>
                    </div>
                  }
                </div>
              }
            </div>

            <div class="mt-2">
              <button type="button" class="btn btn-outline-primary btn-sm" [disabled]="!practice.get('practiceDate')?.value" (click)="addRow(pi)">
                <i class="simple-icon-plus mr-1"></i>{{ 'practicePlan.addRow' | translate }}
              </button>
            </div>
          </div>
        }
      </div>

      <div class="mt-3">
        <button type="button" class="btn btn-outline-primary" (click)="addPractice()">
          <i class="simple-icon-plus mr-1"></i>{{ 'practicePlan.addPractice' | translate }}
        </button>
      </div>
    </form>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-outline-primary" (click)="modalRef.hide()">{{ 'modal.cancel' | translate }}</button>
    <button type="button" class="btn btn-primary" [disabled]="submitting || totalRowsCount() === 0" (click)="onSubmit()">
      {{ (totalRowsCount() === 1 ? 'practicePlan.createRecording' : 'practicePlan.createRecordings') | translate: { count: totalRowsCount() } }}
    </button>
  </div>
</ng-template>
